/**
 * Read-only presentation of a therapy request form, shared by the therapy
 * request view and the bid view so both pages read the same way.
 */

// "bloodPressure" / "blood_pressure" -> "Blood Pressure"
const prettyLabel = (key = '') =>
  key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();

const prettyValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export const StatusBadge = ({ status }) => {
  const tones = {
    requested: 'bg-amber-50 text-amber-700 ring-amber-200',
    bid: 'bg-sky-50 text-sky-700 ring-sky-200',
    treating: 'bg-violet-50 text-violet-700 ring-violet-200',
    done: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  };
  if (!status) return null;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ring-1 ring-inset ${
        tones[status] || 'bg-slate-100 text-slate-600 ring-slate-200'
      }`}
    >
      {status}
    </span>
  );
};

export const OriginBadge = ({ origin }) =>
  origin === 'doctor' ? (
    <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600 ring-1 ring-inset ring-slate-200">
      Raised by doctor
    </span>
  ) : null;

const TherapyRequestDetails = ({ requestForm, title = 'Request Details' }) => {
  const entries = Object.entries(requestForm || {});

  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-6 py-4">
        <h2 className="text-sm font-semibold tracking-tight text-slate-900">{title}</h2>
      </div>

      {entries.length === 0 ? (
        <p className="px-6 py-10 text-center text-sm text-slate-400">No details submitted with this request.</p>
      ) : (
        <dl className="grid grid-cols-1 gap-x-8 gap-y-4 px-6 py-5 sm:grid-cols-2 xl:grid-cols-3">
          {entries.map(([key, value]) => (
            <div key={key} className="min-w-0 border-b border-slate-100 pb-3 last:border-b-0">
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{prettyLabel(key)}</dt>
              <dd className="mt-1 break-words text-sm font-medium text-slate-900">{prettyValue(value)}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
};

export default TherapyRequestDetails;
